import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy } from "lucide-react";
import { InterviewRank } from "@/lib/interviewTypes";
import { calculateFinalRank, hasAnyRank } from "@/lib/interviewUtils";
import { Badge } from "@/components/ui/badge";

interface InterviewRankingsProps {
  ranks: InterviewRank;
  title?: string;
  status?: string;
}

const InterviewRankings: React.FC<InterviewRankingsProps> = ({
  ranks,
  title = "Your Interview Rankings",
  status,
}) => {
  const finalRank = calculateFinalRank(ranks);
  const hasRank = hasAnyRank(ranks);

  if (!hasRank) {
    return null;
  }

  const statusLabelMap: Record<
    string,
    {
      label: string;
      badgeVariant?:
        | "default"
        | "secondary"
        | "destructive"
        | "dangerous"
        | "outline";
      className?: string;
    }
  > = {
    completed: { label: "Completed", badgeVariant: "default" },
    scheduled: {
      label: "Scheduled",
      badgeVariant: "secondary",
      className: "border-sky-200 bg-sky-100 text-sky-700 hover:bg-sky-100",
    },
    missed: { label: "Missed", badgeVariant: "destructive" },
    pending: { label: "Pending Review", badgeVariant: "dangerous" },
  };

  const statusBadge = status ? statusLabelMap[status.toLowerCase()] : undefined;

  const formatScore = (score: number) =>
    score > 0 ? `${score} / 10` : "Not Rated";

  const rankModules = [
    {
      label: "HTML & CSS",
      rank: ranks.html_css,
      badgeClass: "bg-sky-100 text-sky-700",
      progressClass: "bg-sky-500",
    },
    {
      label: "JavaScript",
      rank: ranks.javascript,
      badgeClass: "bg-amber-100 text-amber-700",
      progressClass: "bg-amber-500",
    },
    {
      label: "React",
      rank: ranks.react,
      badgeClass: "bg-cyan-100 text-cyan-700",
      progressClass: "bg-cyan-500",
    },
    {
      label: "Backend",
      rank: ranks.backend,
      badgeClass: "bg-red-100 text-red-700",
      progressClass: "bg-red-500",
    },
  ];

  return (
    <Card className="border-red-100 shadow-sm dark:bg-black">
      <CardHeader className="border-b border-red-50 bg-gradient-to-r from-red-50 via-white to-red-50 rounded-tr rounded-md">
        <CardTitle className="text-xl font-bold flex items-center justify-between gap-3">
          <span className="flex items-center gap-2 dark:text-red-500">
            <Trophy className="w-5 h-5 text-yellow-500" />
            {title}
          </span>
          {statusBadge ? (
            <Badge
              variant={statusBadge.badgeVariant}
              className={statusBadge.className}
            >
              {statusBadge.label}
            </Badge>
          ) : null}
        </CardTitle>
        <p className="text-sm text-zinc-500">
          Track how you performed across each interview module and review your
          overall standing.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {rankModules.map((module, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-lg border border-red-100 bg-white dark:bg-black dark:text-gray-100 p-4 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-600 ">
                    Module
                  </p>
                  <h4 className="text-base font-semibold text-zinc-700  dark:text-gray-100">
                    {module.label}
                  </h4>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${module.badgeClass}`}
                >
                  {module.rank > 0 ? "Scored" : "Pending"}
                </span>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span>{module.rank > 0 ? "Score" : "Status"}</span>
                  <span className="font-semibold text-zinc-600 dark:text-gray-100">
                    {formatScore(module.rank)}
                  </span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-red-50">
                  <div
                    className={`${module.progressClass} h-full rounded-full transition-all duration-300`}
                    style={{
                      width:
                        module.rank > 0
                          ? `${Math.min(module.rank, 10) * 10}%`
                          : "0%",
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        {finalRank > 0 && (
          <div className="rounded-xl border border-red-200 bg-gradient-to-r from-red-50 via-red-100 to-red-50 p-6 shadow-sm">
            <div className="flex flex-col gap-2 text-red-700 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide">
                  Overall Average Rank
                </p>
                <h4 className="text-2xl font-bold">
                  Great job! Keep the momentum going.
                </h4>
              </div>
              <div className="text-4xl font-extrabold text-red-600">
                {finalRank.toFixed(1)}{" "}
                <span className="text-lg font-semibold text-red-700">/ 10</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InterviewRankings;
